import {
  padrinhoSummarySchema,
  wallSchema,
  type PadrinhoSummary,
  type Wall,
  type WallRole,
} from '@growup/streaming-shared';
import { Button, Card, Field, Notice, Select, Textarea } from '@growup/ui';
import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';

import { getJson, messageOf, send } from '../api';
import { formatDateTime } from '../format';

const ROLE_LABEL: Record<WallRole, string> = {
  padrinho: 'padrinho',
  streamer: 'streamer',
  staff: 'Stream Staff',
};

/**
 * Mural do grupo (GS12). A padrinho (`padrinho`) writes and comments on their
 * own group's wall; the Stream Staff (`staff`) picks a padrinho, reads the wall
 * and deletes posts or comments.
 */
export function WallView({ mode }: { mode: 'staff' | 'padrinho' }) {
  const [padrinhos, setPadrinhos] = useState<PadrinhoSummary[]>([]);
  const [padrinhoId, setPadrinhoId] = useState('');
  const [wall, setWall] = useState<Wall | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mode !== 'staff') return;
    getJson('/api/streaming/padrinhos', z.array(padrinhoSummarySchema))
      .then((list) => {
        setPadrinhos(list);
        setPadrinhoId((current) => current || (list[0]?.accountId ?? ''));
      })
      .catch((e: unknown) => setError(messageOf(e)));
  }, [mode]);

  const path =
    mode === 'padrinho'
      ? '/api/streaming/padrinho/wall'
      : padrinhoId === ''
        ? null
        : `/api/streaming/walls/${padrinhoId}`;

  const load = useCallback(() => {
    if (path === null) return;
    setError(null);
    getJson(path, wallSchema)
      .then(setWall)
      .catch((e: unknown) => setError(messageOf(e)));
  }, [path]);
  useEffect(load, [load]);

  const act = async (run: () => Promise<unknown>) => {
    setError(null);
    try {
      const result = await run();
      if (result !== undefined && result !== null) setWall(wallSchema.parse(result));
      else load();
    } catch (e) {
      setError(messageOf(e));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {mode === 'staff' ? (
        padrinhos.length === 0 ? (
          <p className="text-gu-ink-muted">Ainda ninguém tem o papel Padrinho/Madrinha.</p>
        ) : (
          <Field label="Mural de" className="max-w-sm">
            <Select value={padrinhoId} onChange={(e) => setPadrinhoId(e.target.value)}>
              {padrinhos.map((p) => (
                <option key={p.accountId} value={p.accountId}>
                  {p.login}
                </option>
              ))}
            </Select>
          </Field>
        )
      ) : (
        <Composer
          label="Escrever no mural do grupo"
          max={2000}
          onSend={(body) => act(() => send('POST', '/api/streaming/padrinho/wall', { body }))}
        />
      )}
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {wall && wall.posts.length === 0 ? (
        <p className="text-gu-ink-muted">O mural ainda está vazio.</p>
      ) : null}
      {wall?.posts.map((p) => (
        <Card
          key={p.id}
          headingLevel={2}
          eyebrow={`${p.author} · ${ROLE_LABEL[p.role]} · ${formatDateTime(p.createdAt)}`}
          actions={
            mode === 'staff' ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void act(() => send('DELETE', `/api/streaming/wall-posts/${p.id}`))}
              >
                Apagar
              </Button>
            ) : undefined
          }
        >
          <p className="whitespace-pre-line">{p.body}</p>
          <ul className="flex flex-col gap-2 border-l-2 border-gu-line pl-3">
            {p.comments.map((c) => (
              <li key={c.id} className="flex flex-col">
                <span className="text-caption text-gu-ink-muted">
                  {c.author} · {ROLE_LABEL[c.role]} · {formatDateTime(c.createdAt)}
                  {mode === 'staff' ? (
                    <>
                      {' · '}
                      <button
                        type="button"
                        className="gu-link"
                        onClick={() =>
                          void act(() => send('DELETE', `/api/streaming/wall-comments/${c.id}`))
                        }
                      >
                        apagar
                      </button>
                    </>
                  ) : null}
                </span>
                <span className="whitespace-pre-line">{c.body}</span>
              </li>
            ))}
          </ul>
          {mode === 'padrinho' ? (
            <Composer
              label="Comentário"
              max={1000}
              compact
              onSend={(body) =>
                act(() => send('POST', `/api/streaming/padrinho/wall/${p.id}/comments`, { body }))
              }
            />
          ) : null}
        </Card>
      ))}
    </div>
  );
}

function Composer({
  label,
  max,
  compact = false,
  onSend,
}: {
  label: string;
  max: number;
  compact?: boolean;
  onSend: (body: string) => Promise<void>;
}) {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    await onSend(body);
    setBody('');
    setBusy(false);
  };
  return (
    <div className="flex flex-col gap-2">
      <Field label={label}>
        <Textarea
          value={body}
          rows={compact ? 2 : 4}
          maxLength={max}
          onChange={(e) => setBody(e.target.value)}
        />
      </Field>
      <div>
        <Button
          size="sm"
          variant={compact ? 'secondary' : 'primary'}
          disabled={busy || body.trim() === ''}
          onClick={() => void submit()}
        >
          {compact ? 'Comentar' : 'Publicar'}
        </Button>
      </div>
    </div>
  );
}
